import React, { useEffect, useState } from "react";
import axios from "axios";
import Apiurl from "../environment/Apiurl";
import { Search, Edit, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const LabManagement = () => {
  const [responseData, setResponseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination & Search
  const [activePage, setActivePage] = useState(1);
  const [showEntries, setShowEntries] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Form States
  const [addData, setAddData] = useState({
    lab_report_id: "",
    lab_source: "",
    lab_name: "",
    report_date: "",
    report_status: "",
    patient_id: "",
    referred_by: "",
  });
  const [updateData, setUpdateData] = useState({});
  const [deleteId, setDeleteId] = useState(null);

  const hasPermission = (action) => true;

  const fetchresponseData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${Apiurl}/lab_management`);
      setResponseData(response.data);
      console.log("response data", response.data);
    } catch (err) {
      setError("Failed to fetch responseData");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchresponseData();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const filteredData = responseData.filter((lab) =>
    Object.values(lab)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const totalPages =
    showEntries === "All" ? 1 : Math.ceil(filteredData.length / showEntries);

  const indexOfLastItem =
    showEntries === "All" ? filteredData.length : activePage * showEntries;
  const indexOfFirstItem =
    showEntries === "All" ? 0 : indexOfLastItem - showEntries;
  const currentItems =
    showEntries === "All"
      ? filteredData
      : filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${Apiurl}/lab_management`, addData);
      fetchresponseData();
      setAddData({
        lab_report_id: "",
        lab_source: "",
        lab_name: "",
        report_date: "",
        report_status: "",
        patient_id: "",
        referred_by: "",
      });
      setShowAddModal(false);
    } catch {
      setError("Failed to add lab");
    }
  };

  // Edit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${Apiurl}/lab_management/${updateData._id}`, updateData);
      fetchresponseData();
      setShowEditModal(false);
    } catch {
      setError("Failed to update lab");
    }
  };

  // Delete Confirm
  const confirmDelete = async () => {
    try {
      await axios.delete(`${Apiurl}/lab_management/${deleteId}`);
      fetchresponseData();
      setShowDeleteModal(false);
    } catch {
      setError("Failed to delete lab");
    }
  };

  // useNavigate

  const navigate = useNavigate();

  const navigateAddPage = () => {
    navigate("/add-lab-management");
  };

  const navigateUpdatePage = (id) => {
    navigate(`/update-lab-management/${id}`);
  };

  return (
    <div className="p-3">
      {/* ✅ Add Button */}
      {hasPermission("create") && (
        <div className="text-end">
          <button
            className="btn btn-sm text-white"
            style={{ backgroundColor: "#01AEBE" }}
            onClick={navigateAddPage}
          >
            + Add Lab
          </button>
        </div>
      )}

      {/* ✅ Error Message */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show mt-2">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {/* ✅ Table */}
      {hasPermission("read") && (
        <div className="card card-body mt-2">
          <div className="bg-white rounded">
            <div className="row mb-3 crm-table-toolbar">
              {/* Show Entries */}
              <div className="col-md-2">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-table"></i>
                  </span>
                  <select
                    className="form-select"
                    value={showEntries}
                    onChange={(e) => setShowEntries(e.target.value)}
                  >
                    <option value="All">Show All</option>
                    {[5, 10, 20, 50, 100, 200, 500, 1000].map((n) => (
                      <option key={n} value={n}>
                        Show {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Search */}
              <div className="col-md-3 offset-md-7">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control pe-5"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search
                    size={16}
                    className="position-absolute text-muted"
                    style={{
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              </div>
            </div>

            {loading && <div className="text-center py-3">Loading...</div>}

            <div className="table-responsive">
              <table className="table responsive-table">
                <thead className="table-light">
                  <tr style={{ backgroundColor: "#01AEBE", color: "white" }}>
                    <th>S.No</th>
                    <th>Report ID</th>
                    <th>Lab Source</th>
                    <th>Lab Name</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Patient ID</th>
                    <th>Referred By</th>
                    {(hasPermission("update") || hasPermission("delete")) && (
                      <th>Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((lab, index) => (
                      <tr key={lab._id}>
                        <td>{indexOfFirstItem + index + 1}</td>
                        <td>{lab.lab_report_id}</td>
                        <td>
                          {lab.lab_source === "Internal" && (
                            <span className="badge bg-success">
                              <i className="bi bi-box-arrow-in-right me-1"></i>{" "}
                              {lab.lab_source}
                            </span>
                          )}
                          {lab.lab_source === "External" && (
                            <span className="badge bg-danger">
                              <i className="bi bi-box-arrow-up-right me-1"></i>{" "}
                              {lab.lab_source}
                            </span>
                          )}
                        </td>
                        <td>{lab.lab_name}</td>
                        <td>{lab.report_date}</td>
                        <td>
                          {lab.report_status === "Completed" && (
                            <span className="badge bg-success">
                              <i className="bi bi-check-circle me-1"></i>{" "}
                              {lab.report_status}
                            </span>
                          )}
                          {lab.report_status === "Pending" && (
                            <span className="badge bg-warning text-white">
                              <i className="bi bi-hourglass-split me-1"></i>{" "}
                              {lab.report_status}
                            </span>
                          )}
                          {lab.report_status === "Processing" && (
                            <span className="badge bg-secondary">
                              <i className="bi bi-gear me-1"></i>{" "}
                              {lab.report_status}
                            </span>
                          )}
                          {!["Completed", "Pending", "Processing"].includes(
                            lab.report_status
                          ) && (
                            <span className="badge bg-light text-dark">
                              {lab.report_status}
                            </span>
                          )}
                        </td>
                        <td>{lab.patient_id}</td>
                        <td>{lab.referred_by}</td>
                        <td>
                          {hasPermission("delete") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              onClick={() => {
                                setDeleteId(lab._id);
                                setShowDeleteModal(true);
                              }}
                            >
                              <Trash2 size={16} className="text-danger" />
                            </button>
                          )}
                          {hasPermission("update") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              onClick={() => navigateUpdatePage(lab._id)}
                            >
                              <Edit size={16} className="text-primary" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-3">
                        {loading ? "Loading..." : "No responseData found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ✅ Pagination */}
            {filteredData.length > 0 && showEntries !== "All" && (
              <div className="container">
                <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                  {/* Previous Button */}
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() =>
                      activePage > 1 && setActivePage(activePage - 1)
                    }
                    disabled={activePage === 1}
                  >
                    <i className="fas fa-chevron-left"></i>
                    <span className="d-none d-sm-inline">Previous</span>
                  </button>

                  {/* Page Numbers */}
                  <div
                    className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                    style={{ maxWidth: "100%" }}
                  >
                    {pages.map((page, index) => (
                      <button
                        key={index}
                        className={`btn btn-sm px-3 flex-shrink-0 ${
                          page === activePage
                            ? "text-white"
                            : "btn-outline-secondary"
                        }`}
                        style={
                          page === activePage
                            ? { backgroundColor: "#20c997", border: "none" }
                            : {}
                        }
                        onClick={() => setActivePage(page)}
                        disabled={page === "..."}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  {/* Next Button */}
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() =>
                      activePage < totalPages && setActivePage(activePage + 1)
                    }
                    disabled={activePage === totalPages}
                  >
                    <span className="d-none d-sm-inline">Next</span>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ✅ Edit Modal */}
      {showEditModal && (
        <div className="modal fade show" style={{ display: "block" }}>
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6>Edit Lab</h6>
                <button
                  className="btn-close"
                  onClick={() => setShowEditModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleEditSubmit}>
                  <input
                    type="text"
                    name="lab_report_id"
                    placeholder="Report ID"
                    className="form-control mb-2"
                    value={updateData.lab_report_id || ""}
                    onChange={(e) =>
                      setUpdateData({
                        ...updateData,
                        lab_report_id: e.target.value,
                      })
                    }
                    required
                  />
                  <input
                    type="text"
                    name="lab_source"
                    placeholder="Lab Source"
                    className="form-control mb-2"
                    value={updateData.lab_source || ""}
                    onChange={(e) =>
                      setUpdateData({
                        ...updateData,
                        lab_source: e.target.value,
                      })
                    }
                    required
                  />
                  <input
                    type="text"
                    name="lab_name"
                    placeholder="Lab Name"
                    className="form-control mb-2"
                    value={updateData.lab_name || ""}
                    onChange={(e) =>
                      setUpdateData({
                        ...updateData,
                        lab_name: e.target.value,
                      })
                    }
                    required
                  />
                  <input
                    type="date"
                    name="report_date"
                    className="form-control mb-2"
                    value={updateData.report_date || ""}
                    onChange={(e) =>
                      setUpdateData({
                        ...updateData,
                        report_date: e.target.value,
                      })
                    }
                    required
                  />
                  <input
                    type="text"
                    name="report_status"
                    placeholder="Status"
                    className="form-control mb-2"
                    value={updateData.report_status || ""}
                    onChange={(e) =>
                      setUpdateData({
                        ...updateData,
                        report_status: e.target.value,
                      })
                    }
                    required
                  />
                  <input
                    type="text"
                    name="patient_id"
                    placeholder="Patient ID"
                    className="form-control mb-2"
                    value={updateData.patient_id || ""}
                    onChange={(e) =>
                      setUpdateData({
                        ...updateData,
                        patient_id: e.target.value,
                      })
                    }
                    required
                  />
                  <input
                    type="text"
                    name="referred_by"
                    placeholder="Referred By"
                    className="form-control mb-2"
                    value={updateData.referred_by || ""}
                    onChange={(e) =>
                      setUpdateData({
                        ...updateData,
                        referred_by: e.target.value,
                      })
                    }
                    required
                  />
                  <div className="text-center">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm me-2"
                      onClick={() => setShowEditModal(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-success btn-sm">
                      Update
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Delete Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block" }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center">
                <p>Are you sure you want to delete this Lab?</p>
                <div className="d-flex justify-content-center gap-2">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={confirmDelete}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showAddModal || showEditModal || showDeleteModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
};

export default LabManagement;
